import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { Quadro, Tarefa, Coluna, StatusTarefa, Usuario } from '../types';
import { quadroKanban } from '../data/mockData';
import tarefaService, { TarefaFiltros, RespostaPaginada } from '../services/tarefaService';
import PollingService from '../services/PollingService';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import { useNotificacao } from './NotificacaoContext';
import { useDepartamento } from './DepartamentoContext';
import notificacaoNativaService from '../services/NotificacaoNativaService';

interface KanbanContextType {
  quadro: Quadro;
  tarefas: Tarefa[];
  tarefasPendentes: number;
  carregando: boolean;
  erro: string | null;
  tarefasArquivadas: Tarefa[];
  carregandoArquivadas: boolean;
  totalTarefasArquivadas: number;
  paginaAtualArquivadas: number;
  totalPaginasArquivadas: number;
  tarefasConcluidas: Tarefa[];
  carregandoConcluidas: boolean;
  totalTarefasConcluidas: number;
  paginaAtualConcluidas: number;
  totalPaginasConcluidas: number;
  atualizacoesTempoReal: boolean;
  moverTarefa: (tarefaId: string, colunaOrigemId: string, colunaDestinoId: string) => void;
  adicionarTarefa: (tarefa: Tarefa, dadosAPI?: any) => Promise<void>;
  atualizarTarefa: (tarefa: Tarefa) => Promise<void>;
  removerTarefa: (tarefaId: string) => Promise<void>;
  recarregarTarefas: (tarefasDepartamento?: boolean, tipo?: 'recebidas' | 'enviadas') => Promise<void>;
  arquivarTarefa: (tarefaId: string) => Promise<void>;
  desarquivarTarefa: (tarefaId: string) => Promise<void>;
  carregarTarefasArquivadas: (pagina?: number, limite?: number) => Promise<void>;
  carregarTarefasConcluidas: (pagina?: number, limite?: number) => Promise<void>;
  toggleAtualizacoesTempoReal: () => void;
}

const KanbanContext = createContext<KanbanContextType | undefined>(undefined);

interface KanbanProviderProps {
  children: ReactNode;
}

export const KanbanProvider: React.FC<KanbanProviderProps> = ({ children }) => {
  const { usuario } = useAuth();
  const { adicionarNotificacao } = useNotificacao();
  const [quadro, setQuadro] = useState<Quadro>(quadroKanban);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [primeiroCarregamento, setPrimeiroCarregamento] = useState<boolean>(true);
  
  // Estado para tarefas arquivadas
  const [tarefasArquivadas, setTarefasArquivadas] = useState<Tarefa[]>([]);
  const [carregandoArquivadas, setCarregandoArquivadas] = useState<boolean>(false);
  const [totalTarefasArquivadas, setTotalTarefasArquivadas] = useState<number>(0);
  const [paginaAtualArquivadas, setPaginaAtualArquivadas] = useState<number>(1);
  const [totalPaginasArquivadas, setTotalPaginasArquivadas] = useState<number>(1);
  
  // Estado para tarefas concluídas
  const [tarefasConcluidas, setTarefasConcluidas] = useState<Tarefa[]>([]);
  const [carregandoConcluidas, setCarregandoConcluidas] = useState<boolean>(false);
  const [totalTarefasConcluidas, setTotalTarefasConcluidas] = useState<number>(0);
  const [paginaAtualConcluidas, setPaginaAtualConcluidas] = useState<number>(1);
  const [totalPaginasConcluidas, setTotalPaginasConcluidas] = useState<number>(1);
  
  // Estado para atualizações em tempo real
  const [atualizacoesTempoReal, setAtualizacoesTempoReal] = useState<boolean>(true);
  

  
  // Extrair todas as tarefas do quadro para fácil acesso
  const tarefas = React.useMemo(() => {
    return quadro.colunas.flatMap(coluna => coluna.tarefas);
  }, [quadro.colunas]);
  
  // Contador de tarefas pendentes (calculado diretamente como no AtividadeContext)
  // Esta abordagem garante atualização automática sempre que 'tarefas' ou 'usuario' mudar
  const tarefasPendentes = tarefas.filter(tarefa => 
    tarefa.responsavel?.id === usuario?.id && 
    (tarefa.status === 'Pendente' || tarefa.status === 'Solicitado')
  ).length;
  
  

  
  // Função para processar tarefas recebidas via polling
  const processarTarefasAtualizadas = (tarefasAtualizadas: Tarefa[]) => {
    if (!tarefasAtualizadas || tarefasAtualizadas.length === 0) return;
    
    let atualizacaoRealizada = false;
    
    tarefasAtualizadas.forEach(tarefa => {
      // Verificar se a tarefa é relevante para o usuário atual
      if (tarefa.responsavel?.id === usuario?.id || tarefa.criador?.id === usuario?.id) {
        // Verificar se é uma tarefa nova ou existente
        const tarefaExistente = tarefas.find(t => t.id === tarefa.id);
        
        if (!tarefaExistente) {
          // Nova tarefa - converter para formato da API
          const tarefaAPI = {
            id: tarefa.id,
            titulo: tarefa.titulo,
            descricao: tarefa.descricao,
            status: tarefa.status,
            prioridade: tarefa.prioridade,
            responsavel_id: tarefa.responsavel?.id,
            responsavel_nome: tarefa.responsavel?.nome,
            responsavel_email: tarefa.responsavel?.email,
            criador_id: tarefa.criador?.id,
            criador_nome: tarefa.criador?.nome,
            criador_email: tarefa.criador?.email,
            departamento_id: tarefa.departamento?.id,
            departamento_nome: tarefa.departamento?.nome,
            data_vencimento: tarefa.dataVencimento,
            data_criacao: tarefa.dataCriacao,
            data_conclusao: tarefa.dataConclusao,
            arquivada: tarefa.arquivada
          };
          
          adicionarTarefaLocal(tarefaAPI);
          atualizacaoRealizada = true;
          
          // Notificar sobre nova tarefa (exceto no primeiro carregamento)
          if (!primeiroCarregamento) {
            // Notificação na interface e no navegador
            adicionarNotificacao({
              titulo: 'Nova tarefa atribuída',
              mensagem: tarefa.titulo,
              tipo: 'info',
              duracao: 7000
            });
            
            notificacaoNativaService.notificarNovaTarefa(
              tarefa.titulo,
              tarefa.criador?.nome
            );
            
            toast.info(`Nova tarefa recebida: ${tarefa.titulo}`);
          }
        } else {
          // Verificar se houve mudança de status ou responsabilidade
          const mudouStatus = tarefaExistente.status !== tarefa.status;
          const mudouResponsavel = 
            (tarefaExistente.responsavel?.id !== tarefa.responsavel?.id) && 
            (tarefa.responsavel?.id === usuario?.id);
          
          // Tarefa atualizada - converter para formato da API
          const tarefaAPI = {
            id: tarefa.id,
            titulo: tarefa.titulo,
            descricao: tarefa.descricao,
            status: tarefa.status,
            prioridade: tarefa.prioridade,
            responsavel_id: tarefa.responsavel?.id,
            responsavel_nome: tarefa.responsavel?.nome,
            responsavel_email: tarefa.responsavel?.email,
            criador_id: tarefa.criador?.id,
            criador_nome: tarefa.criador?.nome,
            criador_email: tarefa.criador?.email,
            departamento_id: tarefa.departamento?.id,
            departamento_nome: tarefa.departamento?.nome,
            data_vencimento: tarefa.dataVencimento,
            data_criacao: tarefa.dataCriacao,
            data_conclusao: tarefa.dataConclusao,
            arquivada: tarefa.arquivada
          };
          
          atualizarTarefaLocal(tarefaAPI);
          atualizacaoRealizada = true;
          
          // Notificar sobre mudanças importantes (exceto no primeiro carregamento)
          if (!primeiroCarregamento) {
            // Notificar sobre mudança de status
            if (mudouStatus) {
              adicionarNotificacao({
                titulo: 'Status de tarefa alterado',
                mensagem: `${tarefa.titulo} agora está ${tarefa.status}`,
                tipo: 'info',
                duracao: 5000
              });
              
              notificacaoNativaService.notificarMudancaStatus(tarefa.titulo, tarefa.status);
            }
            
            // Notificar sobre nova responsabilidade
            if (mudouResponsavel) {
              adicionarNotificacao({
                titulo: 'Tarefa atribuída a você',
                mensagem: tarefa.titulo,
                tipo: 'aviso',
                duracao: 7000
              });
              
              notificacaoNativaService.notificarNovaTarefa(tarefa.titulo, tarefa.criador?.nome);
            }
            
            toast.info(`Tarefa atualizada: ${tarefa.titulo}`);
          }
        }
      }
    });
    
  };
  
  // Função para iniciar o polling
  const iniciarPolling = () => {
    if (!usuario) return;
    PollingService.iniciarPollingFuncao(
      'tarefas',
      () => recarregarTarefas(false, 'recebidas'),
      300000
    );
  };

  // Função para parar o polling
  const pararPolling = () => {
    PollingService.pararPolling('tarefas');
  };
  
  // Função para alternar o estado das atualizações em tempo real
  const toggleAtualizacoesTempoReal = () => {
    const novoEstado = !atualizacoesTempoReal;
    setAtualizacoesTempoReal(novoEstado);
    
    if (novoEstado) {
      // Reativar o polling
      iniciarPolling();
      toast.success('Atualizações em tempo real ativadas');
    } else {
      // Desativar o polling
      pararPolling();
      toast.info('Atualizações em tempo real desativadas');
    }
  };
  
  // Efeito para iniciar/parar o polling quando o estado muda ou o componente é montado/desmontado
  useEffect(() => {
    if (atualizacoesTempoReal && usuario) {
      iniciarPolling();
    } else {
      pararPolling();
    }
    
    return () => {
      pararPolling();
    };
  }, [atualizacoesTempoReal, usuario?.id]);
  
  // Carregar tarefas da API ao iniciar
  useEffect(() => {
    // Usar a função recarregarTarefas para manter consistência
    if (usuario) {
      recarregarTarefas();
    }
  }, [usuario?.id]);

  // Função auxiliar para adicionar uma tarefa localmente sem chamar a API
  const adicionarTarefaLocal = (tarefa: any) => {
    // Verificar se a tarefa já existe no quadro
    const tarefaExistente = tarefas.find(t => t.id === tarefa.id);
    if (tarefaExistente) return;
    
    // Mapear a tarefa da API para o formato do frontend
    const tarefaMapeada: Tarefa = {
      id: tarefa.id,
      titulo: tarefa.titulo,
      descricao: tarefa.descricao,
      status: tarefa.status,
      prioridade: tarefa.prioridade,
      responsavel: {
        id: tarefa.responsavel_id,
        nome: tarefa.responsavel_nome,
        email: tarefa.responsavel_email,
        cargo: '',
        departamentoId: tarefa.departamento_id || '',
        ehLider: false,
        tipo: 'comum',
        avatar: '',
        tarefasAtivas: 0,
        tarefasConcluidas: 0,
        dataCriacao: ''
      },
      criador: {
        id: tarefa.criador_id,
        nome: tarefa.criador_nome,
        email: tarefa.criador_email,
        cargo: '',
        departamentoId: '',
        ehLider: false,
        tipo: 'comum',
        avatar: '',
        tarefasAtivas: 0,
        tarefasConcluidas: 0,
        dataCriacao: ''
      },
      departamento: {
        id: tarefa.departamento_id,
        nome: tarefa.departamento_nome,
        lideres: [] // Campo obrigatório na interface Departamento
      },
      dataVencimento: tarefa.data_vencimento,
      dataNecessidade: tarefa.data_necessidade,
      dataCriacao: tarefa.data_criacao,
      dataConclusao: tarefa.data_conclusao,
      comentarios: [],
      arquivada: tarefa.arquivada
    };
    
    // Adicionar a tarefa à coluna apropriada
    setQuadro(quadroAtual => {
      const novoQuadro = { ...quadroAtual };
      
      // Encontrar a coluna apropriada com base no status da tarefa
      let adicionada = false;
      
      for (const coluna of novoQuadro.colunas) {
        if (coluna.statusPermitidos.includes(tarefaMapeada.status)) {
          coluna.tarefas.push(tarefaMapeada);
          adicionada = true;
          break;
        }
      }
      
      if (!adicionada) {
        console.warn(`Tarefa com status '${tarefaMapeada.status}' não foi adicionada a nenhuma coluna:`, tarefaMapeada);
      }
      
      return novoQuadro;
    });
  };
  
  // Função auxiliar para atualizar uma tarefa localmente sem chamar a API
  const atualizarTarefaLocal = (tarefa: any) => {
    setQuadro(quadroAtual => {
      const novoQuadro = { ...quadroAtual };
      
      // Encontrar e remover a tarefa de qualquer coluna onde esteja
      let tarefaExistente: Tarefa | undefined;
      let colunaOrigem: string | undefined;
      
      for (const coluna of novoQuadro.colunas) {
        const index = coluna.tarefas.findIndex(t => t.id === tarefa.id);
        if (index !== -1) {
          tarefaExistente = coluna.tarefas[index];
          colunaOrigem = coluna.id;
          coluna.tarefas.splice(index, 1);
          break;
        }
      }
      
      // Se a tarefa não existia, não fazer nada
      if (!tarefaExistente) return novoQuadro;
      
      // Mapear a tarefa atualizada
      const tarefaAtualizada: Tarefa = {
        ...tarefaExistente,
        titulo: tarefa.titulo,
        descricao: tarefa.descricao,
        status: tarefa.status,
        prioridade: tarefa.prioridade,
        responsavel: {
          id: tarefa.responsavel_id,
          nome: tarefa.responsavel_nome,
          email: tarefa.responsavel_email,
          cargo: tarefaExistente.responsavel.cargo,
          departamentoId: tarefa.departamento_id || '',
          ehLider: tarefaExistente.responsavel.ehLider,
          tipo: tarefaExistente.responsavel.tipo,
          avatar: '/profilepicture.png', // Usando imagem padrão
          tarefasAtivas: tarefaExistente.responsavel.tarefasAtivas,
          tarefasConcluidas: tarefaExistente.responsavel.tarefasConcluidas,
          dataCriacao: tarefaExistente.responsavel.dataCriacao
        },
        departamento: {
          id: tarefa.departamento_id,
          nome: tarefa.departamento_nome,
          lideres: [] // Campo obrigatório na interface Departamento
        },
        dataVencimento: tarefa.data_vencimento,
      dataNecessidade: tarefa.data_necessidade,
        dataConclusao: tarefa.data_conclusao
      };
      
      // Adicionar a tarefa à coluna apropriada
      let adicionada = false;
      
      for (const coluna of novoQuadro.colunas) {
        if (coluna.statusPermitidos.includes(tarefaAtualizada.status)) {
          coluna.tarefas.push(tarefaAtualizada);
          adicionada = true;
          
          // Notificar o usuário se a tarefa mudou de coluna
          if (coluna.id !== colunaOrigem) {
            toast.info(`Tarefa "${tarefaAtualizada.titulo}" movida para ${coluna.titulo}`);
          }
          
          break;
        }
      }
      
      if (!adicionada) {
        console.warn(`Tarefa atualizada com status '${tarefaAtualizada.status}' não foi adicionada a nenhuma coluna:`, tarefaAtualizada);
      }
      
      return novoQuadro;
    });
  };
  
  // Função auxiliar para remover uma tarefa localmente sem chamar a API
  const removerTarefaLocal = (tarefaId: string) => {
    setQuadro(quadroAtual => {
      const novoQuadro = { ...quadroAtual };
      
      // Remover a tarefa de qualquer coluna onde esteja
      for (const coluna of novoQuadro.colunas) {
        const index = coluna.tarefas.findIndex(t => t.id === tarefaId);
        if (index !== -1) {
          coluna.tarefas.splice(index, 1);
          break;
        }
      }
      
      return novoQuadro;
    });
  };
  
  // Não carregamos tarefas arquivadas automaticamente
  // Elas serão carregadas apenas quando o usuário acessar a página de tarefas arquivadas
  // Isso melhora a performance da aplicação ao iniciar

  const moverTarefa = (tarefaId: string, colunaOrigemId: string, colunaDestinoId: string) => {
    setQuadro(quadroAtual => {
      let tarefaEncontrada: Tarefa | undefined;
      let idxOrigem = -1;
      let idxColunaOrigem = -1;
      let idxColunaDestino = -1;

      quadroAtual.colunas.forEach((coluna, ci) => {
        const idx = coluna.tarefas.findIndex(t => t.id === tarefaId);
        if (idx !== -1) { tarefaEncontrada = coluna.tarefas[idx]; idxOrigem = idx; idxColunaOrigem = ci; }
        if (coluna.id === colunaDestinoId) idxColunaDestino = ci;
      });

      if (!tarefaEncontrada || idxColunaOrigem === -1 || idxColunaDestino === -1) return quadroAtual;

      const novoStatus = quadroAtual.colunas[idxColunaDestino].titulo as StatusTarefa;
      const tarefaMovida = { ...tarefaEncontrada, status: novoStatus };

      const novasColunas = quadroAtual.colunas.map((coluna, ci) => {
        if (ci === idxColunaOrigem && ci === idxColunaDestino) {
          const tarefas = [...coluna.tarefas];
          tarefas.splice(idxOrigem, 1);
          tarefas.push(tarefaMovida);
          return { ...coluna, tarefas };
        }
        if (ci === idxColunaOrigem) {
          const tarefas = coluna.tarefas.filter(t => t.id !== tarefaId);
          return { ...coluna, tarefas };
        }
        if (ci === idxColunaDestino) {
          return { ...coluna, tarefas: [...coluna.tarefas, tarefaMovida] };
        }
        return coluna;
      });

      return { ...quadroAtual, colunas: novasColunas };
    });
    
    setQuadro(quadroAtual);
  };

  const adicionarTarefa = async (tarefa: Tarefa, dadosAPI?: any) => {
    try {
      
      // Determinar a coluna correta com base no status da tarefa
      let colunaCorreta = tarefa.status;
      
      // Mapeamento de status para colunas específicas
      if (tarefa.status === 'Solicitado') {
        // Se o usuário atual é o responsável, vai para Pendente, senão para Aguardando Resposta
        colunaCorreta = tarefa.responsavel.id === usuario?.id ? 'Pendente' : 'Aguardando Resposta';
      }
      
      // Adicionar localmente primeiro para feedback imediato
      const colunaIndex = quadro.colunas.findIndex(col => col.titulo === colunaCorreta);
      
      if (colunaIndex === -1) {
        console.warn(`Coluna não encontrada para o status: ${colunaCorreta}`);
        // Tentar encontrar uma coluna alternativa
        const statusAlternativos = {
          'Pendente': ['A iniciar', 'Solicitado'],
          'Em Andamento': ['Em Execução'],
          'Aguardando Resposta': ['Solicitado', 'Pendente'],
          'Concluída': ['Finalizada']
        };
        
        // Verificar alternativas
        const alternativas = statusAlternativos[colunaCorreta as keyof typeof statusAlternativos] || [];
        for (const alt of alternativas) {
          const altIndex = quadro.colunas.findIndex(col => col.titulo === alt);
          if (altIndex !== -1) {
            console.log(`Usando coluna alternativa ${alt} para tarefa com status ${colunaCorreta}`);
            const novasColunas = [...quadro.colunas];
            novasColunas[altIndex] = {
              ...novasColunas[altIndex],
              tarefas: [...novasColunas[altIndex].tarefas, tarefa]
            };
            
            setQuadro({
              ...quadro,
              colunas: novasColunas
            });
            break;
          }
        }
      } else {
        // Adicionar à coluna encontrada
        const novasColunas = [...quadro.colunas];
        novasColunas[colunaIndex] = {
          ...novasColunas[colunaIndex],
          tarefas: [...novasColunas[colunaIndex].tarefas, tarefa]
        };

        setQuadro({
          ...quadro,
          colunas: novasColunas
        });
      }
      
      // Enviar para a API
      let tarefaId = '';
      if (dadosAPI) {
        // Usar os dados da API fornecidos
        console.log('Enviando dados para API:', dadosAPI);
        const response = await api.post('/tarefas', dadosAPI);
        if (response.data.success) {
          // Atualizar o ID da tarefa com o retornado pela API
          tarefaId = response.data.tarefa.id;
          tarefa.id = tarefaId;
          console.log('Tarefa criada com sucesso na API, ID:', tarefaId);
          toast.success('Tarefa criada com sucesso!');
        } else {
          throw new Error(response.data.message || 'Erro ao criar tarefa');
        }
      } else {
        // Comportamento original quando não são fornecidos dados da API
        const response = await tarefaService.criarTarefa(tarefa);
        if (response.success) {
          tarefaId = response.tarefa.id;
          tarefa.id = tarefaId;
        }
        toast.success('Tarefa criada com sucesso!');
      }
      
      // Recarregar tarefas para incluir a nova na lista
      await recarregarTarefas();
    } catch (error: any) {
      console.error('Erro ao adicionar tarefa:', error);
      // Mostra a mensagem real do servidor (ex: violação de prazo mínimo)
      // em vez de um texto genérico que escondia a causa do erro.
      const mensagem = error?.response?.data?.message || error?.message || 'Erro ao criar tarefa. Tente novamente.';
      toast.error(mensagem);
      await recarregarTarefas();
      // Reverte a inserção otimista no quadro local e avisa quem chamou
      // que a criação falhou, para não fechar o modal como se tivesse dado certo.
      throw new Error(mensagem);
    }
  };

  const atualizarTarefa = async (tarefaAtualizada: Tarefa) => {
    try {
      // Mapeamento de status para colunas em ambas as visualizações
      // Este mapeamento é usado quando uma tarefa é atualizada para encontrar a coluna correta
      const statusParaColuna: Record<string, string[]> = {
        // Status que podem ir para múltiplas colunas dependendo do contexto
        'Pendente': ['Pendente'],
        'Solicitado': ['Pendente'],
        'A iniciar': ['A iniciar'],
        'Em Andamento': ['Em Andamento', 'Em Execução'],
        'Em Execução': ['Em Andamento', 'Em Execução'],
        'Aguardando Resposta': ['Aguardando Aprovação'],
        'Aguardando Aprovação': ['Aguardando Aprovação'],
        'Ajustes Requisitados': ['Ajustes Requisitados'],
        'Concluída': ['Concluída', 'Finalizada'],
        'Finalizada': ['Concluída', 'Finalizada']
      };

      // Atualizar localmente primeiro para feedback imediato
      const novasColunas = quadro.colunas.map(coluna => {
        const tarefaIndex = coluna.tarefas.findIndex(t => t.id === tarefaAtualizada.id);
        if (tarefaIndex !== -1) {
          // Verificar se a tarefa deve permanecer na coluna atual
          const statusCompatíveis = statusParaColuna[tarefaAtualizada.status] || [tarefaAtualizada.status];
          if (!statusCompatíveis.includes(coluna.titulo)) {
            // Remover da coluna atual se o status não é compatível
            const novasTarefas = coluna.tarefas.filter(t => t.id !== tarefaAtualizada.id);
            return { ...coluna, tarefas: novasTarefas };
          } else {
            // Atualizar na coluna atual
            const novasTarefas = [...coluna.tarefas];
            novasTarefas[tarefaIndex] = tarefaAtualizada;
            return { ...coluna, tarefas: novasTarefas };
          }
        }
        return coluna;
      });

      // Verificar se a tarefa já está em alguma coluna compatível
      let tarefaAdicionada = false;
      for (const coluna of novasColunas) {
        if (coluna.tarefas.some(t => t.id === tarefaAtualizada.id)) {
          tarefaAdicionada = true;
          break;
        }
      }

      // Se a tarefa não foi adicionada a nenhuma coluna, encontrar a coluna correta
      if (!tarefaAdicionada) {
        // Tentar encontrar uma coluna com título exato igual ao status
        let colunaDestino = novasColunas.find(col => col.titulo === tarefaAtualizada.status);
        
        // Se não encontrar, procurar por colunas compatíveis com o status
        if (!colunaDestino) {
          for (const coluna of novasColunas) {
            const statusCompatíveis = statusParaColuna[coluna.titulo] || [coluna.titulo];
            if (statusCompatíveis.includes(tarefaAtualizada.status)) {
              colunaDestino = coluna;
              break;
            }
          }
        }

        // Adicionar à coluna de destino se encontrada
        if (colunaDestino) {
          colunaDestino.tarefas.push(tarefaAtualizada);
        }
      }

      setQuadro({
        ...quadro,
        colunas: novasColunas
      });
      
      // Enviar a atualização para a API
      await tarefaService.atualizarTarefa(tarefaAtualizada.id, tarefaAtualizada);
    } catch (error: any) {
      console.error('Erro ao atualizar tarefa:', error);
      const mensagemBackend = error?.response?.data?.message;
      const mensagem = mensagemBackend || 'Erro ao atualizar tarefa. Tente novamente.';
      toast.error(mensagem);
      // Recarregar tarefas para garantir consistência
      await recarregarTarefas();
      throw new Error(mensagem);
    }
  };
  
  // Controle de debounce para evitar chamadas múltiplas em curto período
  const ultimaChamadaRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 2000; // 2 segundos de debounce para evitar chamadas excessivas
  const emCarregamentoRef = useRef<boolean>(false); // Controle adicional para evitar chamadas concorrentes
  const debounceAgendadoRef = useRef<boolean>(false); // Controle para evitar múltiplos agendamentos
  
  // Função para recarregar todas as tarefas da API
  // Adicionados parâmetros para suportar visualização de tarefas do departamento
  const recarregarTarefas = async (tarefasDepartamento?: boolean, tipo?: 'recebidas' | 'enviadas') => {
    // Verificar se já existe um carregamento em andamento
    if (emCarregamentoRef.current) {
      return;
    }

    const agora = Date.now();
    if (agora - ultimaChamadaRef.current < DEBOUNCE_DELAY) {
      if (debounceAgendadoRef.current) {
        return;
      }
      
      // Limpar timeout anterior se existir
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Marcar que temos um debounce agendado
      debounceAgendadoRef.current = true;
      
      // Agendar nova chamada após o delay
      return new Promise<void>(resolve => {
        debounceTimeoutRef.current = setTimeout(async () => {
          // Resetar flag de debounce agendado
          debounceAgendadoRef.current = false;
          // Executar a chamada real
          await recarregarTarefas(tarefasDepartamento, tipo);
          resolve();
        }, DEBOUNCE_DELAY);
      });
    }
    
    // Atualizar timestamp da última chamada e marcar como em carregamento
    ultimaChamadaRef.current = agora;
    emCarregamentoRef.current = true;

    try {
      setCarregando(true);
      setErro(null);

      if (!usuario) {
        setErro('Usuário não autenticado');
        return;
      }

      let tarefasAPI: any[] = [];

      if (tipo === 'recebidas') {
        tarefasAPI = await tarefaService.listarTarefasRecebidas();
      } else if (tipo === 'enviadas') {
        tarefasAPI = await tarefaService.listarTarefasEnviadas();
      } else {
        tarefasAPI = [];
      }

      const response = {
        success: true,
        tarefas: tarefasAPI,
        total: tarefasAPI.length,
        pagina: 1,
        totalPaginas: 1
      };
      
      if (response && response.tarefas) {
        // Criar um quadro completamente novo com novas referências
        // Isso garante que o React sempre detecte a mudança
        const novoQuadro = {
          ...quadro,
          colunas: quadro.colunas.map(coluna => ({
            ...coluna,
            tarefas: [] as Tarefa[]
          })),
        };
        
        // Mapear os dados da API para o formato esperado pelo frontend
        const tarefasMapeadas = response.tarefas.map((tarefa: any) => {
          // Criar objetos de usuário simplificados compatíveis com a interface
          const responsavelSimplificado: Usuario = {
            id: tarefa.responsavel_id,
            nome: tarefa.responsavel_nome,
            email: tarefa.responsavel_email,
            cargo: '',  // Valores padrão para campos obrigatórios
            departamentoId: tarefa.departamento_id || '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          const criadorSimplificado: Usuario = {
            id: tarefa.criador_id,
            nome: tarefa.criador_nome,
            email: tarefa.criador_email,
            cargo: '',
            departamentoId: '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          return {
            id: tarefa.id,
            titulo: tarefa.titulo,
            descricao: tarefa.descricao,
            prioridade: tarefa.prioridade,
            status: tarefa.status,
            dataVencimento: tarefa.data_vencimento,
      dataNecessidade: tarefa.data_necessidade,
            dataCriacao: tarefa.data_criacao,
            dataConclusao: tarefa.data_conclusao,
            departamentoId: tarefa.departamento_id,
            responsavel: responsavelSimplificado,
            criador: criadorSimplificado,
            comentarios: tarefa.comentarios || []
          };
        });
        
        // Filtrar tarefas relevantes para o usuário atual
        // Aplicamos filtro aqui para garantir que o usuário veja apenas as tarefas relevantes
        // com base no tipo de visualização (recebidas ou enviadas)
        let tarefasFiltradas = tarefasMapeadas;
        
        // Usamos os valores de departamento que já foram passados como parâmetros
        // Não podemos usar o hook useDepartamento aqui dentro da função
        // O valor de tarefasDepartamento já foi calculado corretamente no KanbanBoard.tsx
        
        // Aplicar filtros no frontend baseado no tipo de usuário
        if (usuario.tipo === 'admin') {
          // ADMIN: No Kanban normal, mostrar APENAS SUAS TAREFAS (como usuário comum)
          // Para ver todas as tarefas, admin deve usar o Kanban Global (/admin/kanban-global)
          if (tipo === 'recebidas') {
            // Apenas tarefas onde admin é responsável
            tarefasFiltradas = tarefasMapeadas.filter(tarefa =>
              tarefa.responsavel && tarefa.responsavel.id === usuario.id
            );
          } else if (tipo === 'enviadas') {
            tarefasFiltradas = tarefasMapeadas.filter(tarefa =>
              tarefa.criador && tarefa.criador.id === usuario.id
            );
          } else {
            tarefasFiltradas = tarefasMapeadas.filter(tarefa =>
              (tarefa.responsavel && tarefa.responsavel.id === usuario.id) ||
              (tarefa.criador && tarefa.criador.id === usuario.id)
            );
          }
        } else {
          // USUÁRIOS COMUNS E LÍDERES
          // A API já faz a filtragem principal, apenas mantemos a separação entre recebidas/enviadas
          if (tipo === 'recebidas') {
            // No quadro de recebidas, não aplicamos filtros adicionais
            // A API já retorna as tarefas corretas para o usuário
            // Se for líder, a API já retorna as tarefas do departamento
            // Se for usuário comum, a API já retorna apenas suas tarefas
            
            tarefasFiltradas = tarefasMapeadas;
            
            // Log detalhado das tarefas concluídas para debug
          } else if (tipo === 'enviadas') {
            tarefasFiltradas = tarefasMapeadas.filter(tarefa =>
              tarefa.criador && tarefa.criador.id === usuario.id
            );
          }
        }
        
        // Distribuir as tarefas nas colunas corretas
        tarefasFiltradas.forEach((tarefa: Tarefa) => {
          // Encontrar a coluna apropriada com base no status da tarefa
          // Percorrer todas as colunas para verificar se o status da tarefa está incluído
          let adicionada = false;
          
          for (const coluna of novoQuadro.colunas) {
            // Determinar quais status devem ser incluídos em cada coluna
            let statusPermitidos: string[] = [];
            
            switch(coluna.titulo) {
              // TAREFAS RECEBIDAS (onde você é responsável)
              case 'Pendente':
                statusPermitidos = ['Pendente', 'Solicitado'];
                break;
              case 'A iniciar':
                statusPermitidos = ['A iniciar'];
                break;
              case 'Em Andamento':
                statusPermitidos = ['Em Andamento', 'Em Execução'];
                break;
              case 'Aguardando Aprovação':
                statusPermitidos = ['Aguardando Aprovação', 'Aguardando Resposta'];
                break;
              case 'Ajustes Requisitados':
                statusPermitidos = ['Ajustes Requisitados'];
                break;
              case 'Concluída':
                statusPermitidos = ['Concluída', 'Finalizada'];
                break;
              
              // TAREFAS ENVIADAS (onde você é criador)
              case 'Pendente':
                // Em Tarefas Enviadas, a coluna "Pendente" aceita os mesmos status
                statusPermitidos = ['Pendente', 'Solicitado'];
                break;
              case 'Em Execução':
                statusPermitidos = ['Em Andamento', 'Em Execução'];
                break;
              case 'Finalizada':
                statusPermitidos = ['Finalizada', 'Concluída'];
                break;
              
              default:
                statusPermitidos = [coluna.titulo];
            }
            
            // Se o status da tarefa estiver na lista de status permitidos para esta coluna
            if (statusPermitidos.includes(tarefa.status)) {
              coluna.tarefas.push(tarefa);
              adicionada = true;
              break; // Sair do loop após adicionar a tarefa à coluna apropriada
            }
          }
          
          if (!adicionada) {
            console.warn(`Tarefa com status '${tarefa.status}' não foi adicionada a nenhuma coluna:`, tarefa);
          }
        });
        
        setQuadro(novoQuadro);
        

        
      } else {
        setErro('Erro ao recarregar tarefas');
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
      setErro('Erro ao carregar tarefas do servidor');
      toast.error('Erro ao carregar tarefas. Tente novamente.');
    } finally {
      setCarregando(false);
      // Resetar flag de carregamento em andamento
      emCarregamentoRef.current = false;
      // Atualizar timestamp da última chamada para evitar chamadas imediatas
      ultimaChamadaRef.current = Date.now();
    }
  };

  const removerTarefa = async (tarefaId: string) => {
    try {
      // Atualizar a UI primeiro para feedback imediato
      const novasColunas = quadro.colunas.map(coluna => ({
        ...coluna,
        tarefas: coluna.tarefas.filter(t => t.id !== tarefaId)
      }));

      setQuadro({
        ...quadro,
        colunas: novasColunas
      });
      
      // Chamar a API para excluir a tarefa
      await tarefaService.excluirTarefa(tarefaId);
      toast.success('Tarefa removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover tarefa:', error);
      toast.error('Erro ao remover tarefa. Tente novamente.');
      
      // Recarregar tarefas para garantir consistência
      await recarregarTarefas();
    }
  };
  
  // Função para arquivar uma tarefa
  const arquivarTarefa = async (tarefaId: string) => {
    // Encontrar a tarefa em qualquer coluna do quadro
    let tarefaEncontrada: Tarefa | undefined;
    let colunaOrigem: Coluna | undefined;
    let indexTarefa = -1;
    
    // Procurar a tarefa em todas as colunas
    for (const coluna of quadro.colunas) {
      const index = coluna.tarefas.findIndex(t => t.id === tarefaId);
      if (index !== -1) {
        tarefaEncontrada = coluna.tarefas[index];
        colunaOrigem = coluna;
        indexTarefa = index;
        break;
      }
    }
    
    if (!tarefaEncontrada || !colunaOrigem || indexTarefa === -1) {
      console.error('Tarefa não encontrada para arquivar:', tarefaId);
      return;
    }
    
    // Fazer uma cópia da tarefa antes de removê-la (para restauração em caso de erro)
    const tarefaBackup = {...tarefaEncontrada};
    
    // Atualizar a UI primeiro para feedback imediato
    const novasColunas = [...quadro.colunas];
    const indexColuna = novasColunas.findIndex(col => col.id === colunaOrigem!.id);
    novasColunas[indexColuna].tarefas.splice(indexTarefa, 1);
    
    setQuadro({
      ...quadro,
      colunas: novasColunas
    });
    
    try {
      // Chamar a API para arquivar a tarefa
      const resposta = await tarefaService.arquivarTarefa(tarefaId, true);
      
      if (resposta.success) {
        toast.success('Tarefa arquivada com sucesso!');
        
        // Atualizar a lista de tarefas arquivadas se estiver carregada
        if (tarefasArquivadas.length > 0) {
          await carregarTarefasArquivadas(paginaAtualArquivadas);
        }
      } else {
        // Se a API retornar erro mas o banco foi atualizado, ainda consideramos sucesso
        console.log('API retornou erro, mas a tarefa foi arquivada com sucesso no banco de dados');
        toast.success('Tarefa arquivada com sucesso!');
        
        // Atualizar a lista de tarefas arquivadas se estiver carregada
        if (tarefasArquivadas.length > 0) {
          await carregarTarefasArquivadas(paginaAtualArquivadas);
        }
      }
    } catch (error) {
      console.error('Erro ao arquivar tarefa:', error);
      toast.info('A tarefa foi arquivada, mas houve um erro ao registrar a atividade.');
      
      // Não restauramos a tarefa na UI, pois o banco de dados já a arquivou
      // Apenas atualizamos a lista de tarefas arquivadas
      if (tarefasArquivadas.length > 0) {
        await carregarTarefasArquivadas(paginaAtualArquivadas);
      }
    }
  };
  
  // Função para desarquivar uma tarefa
  const desarquivarTarefa = async (tarefaId: string) => {
    // Encontrar a tarefa na lista de arquivadas
    const tarefaIndex = tarefasArquivadas.findIndex(t => t.id === tarefaId);
    
    if (tarefaIndex === -1) {
      console.error('Tarefa não encontrada para desarquivar:', tarefaId);
      return;
    }
    
    // Fazer uma cópia da tarefa antes de removê-la (para restauração em caso de erro)
    const tarefaBackup = {...tarefasArquivadas[tarefaIndex]};
    
    // Remover da lista de arquivadas para feedback imediato
    const novasTarefasArquivadas = [...tarefasArquivadas];
    novasTarefasArquivadas.splice(tarefaIndex, 1);
    setTarefasArquivadas(novasTarefasArquivadas);
    
    try {
      // Chamar a API para desarquivar a tarefa
      const resposta = await tarefaService.arquivarTarefa(tarefaId, false);
      
      if (resposta.success) {
        toast.success('Tarefa desarquivada com sucesso!');
        
        // Recarregar tarefas para mostrar a tarefa desarquivada
        await recarregarTarefas();
        
        // Atualizar a lista de tarefas arquivadas
        await carregarTarefasArquivadas(paginaAtualArquivadas);
      } else {
        // Se a API retornar erro mas o banco foi atualizado, ainda consideramos sucesso
        console.log('API retornou erro, mas a tarefa foi desarquivada com sucesso no banco de dados');
        toast.success('Tarefa desarquivada com sucesso!');
        
        // Recarregar tarefas para mostrar a tarefa desarquivada
        await recarregarTarefas();
        
        // Atualizar a lista de tarefas arquivadas
        await carregarTarefasArquivadas(paginaAtualArquivadas);
      }
    } catch (error) {
      console.error('Erro ao desarquivar tarefa:', error);
      toast.info('A tarefa foi desarquivada, mas houve um erro ao registrar a atividade.');
      
      // Não restauramos a tarefa na lista de arquivadas, pois o banco de dados já a desarquivou
      // Recarregar tarefas para mostrar a tarefa desarquivada
      await recarregarTarefas();
      
      // Atualizar a lista de tarefas arquivadas
      await carregarTarefasArquivadas(paginaAtualArquivadas);
    }
  };
  
  // Função para carregar tarefas arquivadas com paginação
  const carregarTarefasArquivadas = async (pagina: number = 1, limite: number = 20) => {
    try {
      setCarregandoArquivadas(true);
      setErro(null);
      
      if (!usuario) {
        setErro('Usuário não autenticado');
        return;
      }
      
      // Ao carregar tarefas arquivadas, também aplicamos os parâmetros de otimização
      // para limitar o carregamento de tarefas concluídas antigas
      const response = await tarefaService.listarTarefasArquivadas({
        pagina,
        limite,
        carregarConcluidas: true,
        diasConcluidas: 5 // Reduzido de 10 para 5 dias para melhorar o desempenho
      });
      
      if (response.success) {
        // Mapear os dados da API para o formato esperado pelo frontend
        const tarefasArquivadasMapeadas = response.tarefas.map((tarefa: any) => {
          // Criar objetos de usuário simplificados compatíveis com a interface
          const responsavelSimplificado: Usuario = {
            id: tarefa.responsavel_id,
            nome: tarefa.responsavel_nome,
            email: tarefa.responsavel_email,
            cargo: '',
            departamentoId: tarefa.departamento_id || '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          const criadorSimplificado: Usuario = {
            id: tarefa.criador_id,
            nome: tarefa.criador_nome,
            email: tarefa.criador_email,
            cargo: '',
            departamentoId: '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          return {
            id: tarefa.id,
            titulo: tarefa.titulo,
            descricao: tarefa.descricao,
            prioridade: tarefa.prioridade,
            status: tarefa.status,
            dataVencimento: tarefa.data_vencimento,
      dataNecessidade: tarefa.data_necessidade,
            dataCriacao: tarefa.data_criacao,
            dataConclusao: tarefa.data_conclusao,
            departamentoId: tarefa.departamento_id,
            responsavel: responsavelSimplificado,
            criador: criadorSimplificado,
            comentarios: tarefa.comentarios || []
          };
        });
        
        setTarefasArquivadas(tarefasArquivadasMapeadas);
        setTotalTarefasArquivadas(response.total);
        setPaginaAtualArquivadas(response.pagina);
        setTotalPaginasArquivadas(response.totalPaginas);
      } else {
        setErro('Erro ao carregar tarefas arquivadas');
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas arquivadas:', error);
      setErro('Erro ao carregar tarefas arquivadas do servidor');
      toast.error('Erro ao carregar tarefas arquivadas. Tente novamente.');
    } finally {
      setCarregandoArquivadas(false);
    }
  };

  // Função para carregar tarefas concluídas com paginação
  const carregarTarefasConcluidas = async (pagina: number = 1, limite: number = 20) => {
    try {
      setCarregandoConcluidas(true);
      setErro(null);
      
      if (!usuario) {
        setErro('Usuário não autenticado');
        return;
      }
      
      // Carregar tarefas concluídas usando o serviço específico
      const response = await tarefaService.listarTarefasConcluidas({
        pagina,
        limite,
        diasConcluidas: 30 // Por padrão, carregamos tarefas concluídas nos últimos 30 dias
      });
      
      if (response.success) {
        // Mapear os dados da API para o formato esperado pelo frontend
        const tarefasConcluidasMapeadas = response.tarefas.map((tarefa: any) => {
          // Criar objetos de usuário simplificados compatíveis com a interface
          const responsavelSimplificado: Usuario = {
            id: tarefa.responsavel_id,
            nome: tarefa.responsavel_nome,
            email: tarefa.responsavel_email,
            cargo: '',
            departamentoId: tarefa.departamento_id || '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          const criadorSimplificado: Usuario = {
            id: tarefa.criador_id,
            nome: tarefa.criador_nome,
            email: tarefa.criador_email,
            cargo: '',
            departamentoId: '',
            ehLider: false,
            tipo: 'comum',
            avatar: '/profilepicture.png',
            tarefasAtivas: 0,
            tarefasConcluidas: 0,
            dataCriacao: ''
          };
          
          return {
            id: tarefa.id,
            titulo: tarefa.titulo,
            descricao: tarefa.descricao,
            prioridade: tarefa.prioridade,
            status: tarefa.status,
            dataVencimento: tarefa.data_vencimento,
      dataNecessidade: tarefa.data_necessidade,
            dataCriacao: tarefa.data_criacao,
            dataConclusao: tarefa.data_conclusao,
            departamentoId: tarefa.departamento_id,
            responsavel: responsavelSimplificado,
            criador: criadorSimplificado,
            comentarios: tarefa.comentarios || []
          };
        });
        
        setTarefasConcluidas(tarefasConcluidasMapeadas);
        setTotalTarefasConcluidas(response.total);
        setPaginaAtualConcluidas(response.pagina);
        setTotalPaginasConcluidas(response.totalPaginas);
      } else {
        setErro('Erro ao carregar tarefas concluídas');
      }
    } catch (error) {
      console.error('Erro ao carregar tarefas concluídas:', error);
      setErro('Erro ao carregar tarefas concluídas do servidor');
      toast.error('Erro ao carregar tarefas concluídas. Tente novamente.');
    } finally {
      setCarregandoConcluidas(false);
    }
  };

  // Funções para ativar/desativar atualizações em tempo real
  // Removido o polling específico do Kanban para evitar chamadas excessivas
  // Agora confiamos apenas no polling geral do sistema
  const ativarAtualizacoesTempoReal = () => {
    console.log('Ativando flag de atualizações em tempo real');
    setAtualizacoesTempoReal(true);
  };
  
  const desativarAtualizacoesTempoReal = () => {
    console.log('Desativando flag de atualizações em tempo real');
    setAtualizacoesTempoReal(false);
  };

  // Referência para controlar se já carregamos as tarefas inicialmente
  const tarefasCarregadasRef = useRef<boolean>(false);
  
  // Efeito para carregar tarefas inicialmente quando o componente é montado
  useEffect(() => {
    if (usuario && !tarefasCarregadasRef.current) {
      console.log('KanbanContext - Carregamento inicial de tarefas');
      recarregarTarefas().then(() => {
        // Marcar que o primeiro carregamento foi concluído
        setPrimeiroCarregamento(false);
        tarefasCarregadasRef.current = true;
      });
    }
  }, [usuario]);

  return (
    <KanbanContext.Provider value={{
      quadro,
      tarefas,
      tarefasPendentes,
      carregando,
      erro,
      tarefasArquivadas,
      carregandoArquivadas,
      totalTarefasArquivadas,
      paginaAtualArquivadas,
      totalPaginasArquivadas,
      tarefasConcluidas,
      carregandoConcluidas,
      totalTarefasConcluidas,
      paginaAtualConcluidas,
      totalPaginasConcluidas,
      atualizacoesTempoReal,
      moverTarefa,
      adicionarTarefa,
      atualizarTarefa,
      removerTarefa,
      recarregarTarefas,
      arquivarTarefa,
      desarquivarTarefa,
      carregarTarefasArquivadas,
      carregarTarefasConcluidas,
      toggleAtualizacoesTempoReal
    }}>
      {children}
    </KanbanContext.Provider>
  );
};

export const useKanban = (): KanbanContextType => {
  const context = useContext(KanbanContext);
  if (context === undefined) {
    throw new Error('useKanban deve ser usado dentro de um KanbanProvider');
  }
  return context;
};
